import Highlight, { defaultProps, type Language } from 'prism-react-renderer'
import React from 'react'

import { CopyClipboardButton } from 'components/common/CopyClipboardButton'
import Text from 'components/common/Text'
import theme from 'theme'

import 'styles/a11y-light.css'
import 'styles/a11y-dark.css'

type ColorTheme = 'light' | 'dark'

export const CodeWrapper: React.FC<{
  show: boolean
}> = ({ children, show }) => (
  <div
    css={{
      display: show ? 'block' : 'none'
    }}
  >
    {children}
  </div>
)

export const SectionTitle: React.FC<{
  colorTheme?: ColorTheme
}> = ({ colorTheme, ...props }) => (
  <Text
    as="p"
    color="dark"
    css={{
      textAlign: 'left',
      paddingTop: theme.space[3],
      color:
        colorTheme === 'light' ? theme.colors.white[0] : theme.colors.steel[1]
    }}
    {...props}
  />
)

export function formatCode(language, code) {
  /**
   * Don't break shell commands by formatting it
   */
  if (language === 'bash') {
    return code
  }

  /**
   * Allow CSS to Break up long lines into multiple lines on spaces
   *
   * 1. Take the spaces out of places where they already exist after a semi-colon
   *    so that when we add them in step 2, we aren't adding spaces where there are already spaces.
   * 2. Add a space not only to the ones changed in step 1, but to all the other semi-colons that didn't have spaces originally.
   * 3. and 4. Same as 1 and 2, but for commas.
   *
   * CSS has a few options for word-wrap and white-space.
   *
   * 1. Break on spaces
   * 2. Break a word
   * 3. Break on any character
   *
   * None of these allow us to keep words and code phrases together without sometimes having a horizontal scrollbar.
   * By adding the spaces after commas and semi-colons, we are allowing the css to break on spaces without more javascript trickery.
   */

  try {
    /**
     * Prettify JSON
     */
    return JSON.stringify(JSON.parse(code), null, 2)
  } catch {
    return code
      .replaceAll('; ', ';')
      .replaceAll(';', '; ')
      .replaceAll(', ', ',')
      .replaceAll(',', ', ')
  }
}

export const CodeBlock: React.FC<{
  code: string
  colorTheme?: ColorTheme
  includeLineNumbers?: boolean
  isCopyEnabled?: boolean
  language?: Language
}> = ({
  code,
  colorTheme = 'dark',
  isCopyEnabled = true,
  includeLineNumbers = true,
  language = 'bash',
  ...rest
}) => {
  return (
    <div
      className={`codeblock-${colorTheme}`}
      css={{
        position: 'relative'
      }}
      {...rest}
    >
      <Highlight
        {...defaultProps}
        code={formatCode(language, code)}
        language={language || 'bash'}
        theme={undefined}
      >
        {({ className, style, tokens, getLineProps, getTokenProps }) => {
          /**
           * `tokens` are the numbered lines.
           * In order to make sure the line number column stays a consistent width,
           * Find out how wide the widest column needs to be by determining
           * how many characters are in the last number column.
           *
           * tokens.length = number_of_lines
           * number_of_lines.toString() = number_of_lines_as_string
           * number_of_lines_as_string.length = number of characters in that number (4000 lines = 4 characters wide)
           */
          const lineNumWidth =
            tokens.length.toString().length * 8.5 + theme.space[3] // width of monospace character + padding

          return (
            <pre className={className} style={style}>
              {tokens.map((line, i) => (
                <div
                  key={`line_${i}`}
                  css={{
                    fontSize: theme.fontSizes[1],
                    color:
                      colorTheme === 'light'
                        ? theme.colors.steel[1]
                        : '#abb2bf',
                    background: 'none',
                    display: 'grid',
                    gridAutoColumns: 'max-content auto',
                    columnGap: theme.space[3]
                  }}
                  {...getLineProps({ line, key: i })}
                >
                  {includeLineNumbers && (
                    <div
                      css={{
                        gridColumnStart: 1,
                        userSelect: 'none',
                        textAlign: 'right',
                        opacity: theme.opacity[0],
                        borderRight: theme.border.base,
                        width: lineNumWidth,
                        paddingRight: theme.space[3]
                      }}
                    >
                      {i + 1}
                    </div>
                  )}
                  <div
                    css={{
                      gridColumnStart: 2,
                      whiteSpace: 'break-spaces',
                      textAlign: 'left'
                    }}
                  >
                    {line.map((token, key) => (
                      <span
                        key={`span_${key}`}
                        {...getTokenProps({ token, key })}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </pre>
          )
        }}
      </Highlight>
      {isCopyEnabled && (
        <CopyClipboardButton
          content={code}
          css={{
            top: theme.space[2],
            position: 'absolute',
            right: theme.space[2],
            color:
              colorTheme === 'light'
                ? theme.colors.steel[1]
                : theme.colors.white[0]
          }}
          tooltipText="Copy"
        />
      )}
    </div>
  )
}
